import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SocketMessage, useSocket } from '../../../../connection/socket';
import { BlProfile, BrandliveEvent, ISocketCommentProps, LanguagesAbbr, Session, Status } from '../../../../types/working-model';
import useHasScrolledToBottom from '../../../../utils/use-has-scrolled-to-bottom';
import classNames from 'classnames';

import WaitingIndicator from '../../../general-ui/waiting-indicator/waiting-indicator';
import AddLiveComment from './add-live-comment';
import './live-chat.scss';
import ChatFeed from './chat-feed';
import Promotions from './promotions';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import Icon, { COLORS, ICONS } from '../../../general-ui/icon';
import { TemplateClassNames } from '../../../../types/template-layouts';

import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { useDispatch } from 'react-redux';
import { deleteChatMessage, incomingChatMessage } from '../../../../store/actions/event/chat-actions';
import usePrevious from '../../../../utils/use-previous';
import { useTranslation } from 'react-i18next';

interface ILiveChatProps {
	loading?: boolean;
	session: Session | null;
	language: LanguagesAbbr;
	registrationOn: boolean;
	blProfileUser: BlProfile | null;
	template: TemplateClassNames;
	forceOverlay: boolean;
	mobileOnly: boolean;
	isEditor?: boolean;
	onChatClose: (chatClosed: boolean) => void;
	chatClosed: boolean;
	eventBundle: BrandliveEvent;
	isAdmin?: boolean;
}

let lastHeight = window.innerHeight;
const SCROLL_OFFSET = 40;

const LiveChat: React.FC<ILiveChatProps> = ({
	session,
	loading = false,
	language,
	blProfileUser,
	template,
	forceOverlay,
	mobileOnly,
	isEditor = false,
	onChatClose,
	chatClosed,
	eventBundle,
	isAdmin = false
}) => {

	const { comments, loadingComments } = useTypedSelector(state => state.ChatReducer);

	const dispatch = useDispatch();

	const [extraPadding, setExtraPadding] = useState(false);
	const [showSpamFilterAlert, setShowSpamFilterAlert] = useState(false);
	const [showMoreButton, setShowMoreButton] = useState(false);
	const [moreButtonWidth, setMoreButtonWidth] = useState(0);

	const prevComments = usePrevious(comments);

	const { t } = useTranslation('session');

	const containerRef = useRef<HTMLDivElement | null>(null);
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const moreButtonRef = useRef<HTMLButtonElement | null>(null);

	const socket = useSocket(`session-${session?.uuid}-${language || 'en'}`);

	const { hasScrolledToBottom } = useHasScrolledToBottom({
		scrolledContainerRef: scrollContainerRef?.current,
		offset: SCROLL_OFFSET,
		throttleTime: 500,
	});

	useEffect(() => {
		if (hasScrolledToBottom) {
			setShowMoreButton(false);
		}
	}, [hasScrolledToBottom]);

	useEffect(() => {
		if (prevComments && comments.size > 0 && prevComments.size === 0 && scrollContainerRef.current) {
			scrollContainerRef.current.scrollTop = scrollContainerRef.current?.scrollHeight || 500;
		}
	}, [comments, prevComments]);

	useEffect(() => {
		if (scrollContainerRef.current)
			scrollContainerRef.current.scrollTop = scrollContainerRef.current?.scrollHeight + 200;
	}, []);

	useEffect(() => {
		// add socket listener
		const handleSessionChatComment = (comment: any) => {
			if (comment) {
				// const msgs: ISocketCommentProps[] = [];
				comment.chat_messages.forEach((msg: ISocketCommentProps) => {
					if (!msg?.spam) {
						dispatch(incomingChatMessage(msg));
					}
					// if spam && the comment originates from this user, then show a message
					if (
						msg?.spam
						&& msg.email === blProfileUser?.email
						&& (
							msg?.bl_profile?.toString() === blProfileUser?.bl_profile?.toString()
							|| msg?.bl_profile_non_registered?.toString() === blProfileUser?.bl_profile?.toString()
						)
					) {
						setShowSpamFilterAlert(true);
					}
				});

				if (scrollContainerRef?.current) {
					// auto scroll if user is scrolled to bottom
					// else show more button if scroll height is at least 40 pixels greater than the container height
					if (hasScrolledToBottom) {
						scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
					} else if (scrollContainerRef.current.scrollHeight >= scrollContainerRef.current.clientHeight + SCROLL_OFFSET) {
						setShowMoreButton(true);
					}
				}
			}
		};
		socket.addListener('chat', handleSessionChatComment);
		return () => {
			// remove socket listener
			socket.removeListener('chat', handleSessionChatComment);
		};
	}, [socket, blProfileUser, dispatch, hasScrolledToBottom]);

	useEffect(() => {
		if (containerRef.current) {

			const handleResize = () => {
				setExtraPadding(window.innerHeight > lastHeight);
				lastHeight = window.innerHeight;
			};

			window.addEventListener('resize', handleResize);
			return () => {
				window.removeEventListener('resize', handleResize);
			};
		}
	}, []);

	useEffect(() => {
		const handleModeration = ({ data }: SocketMessage) => {
			switch (data.message_type_update) {
				case Status.Approved: {
					return dispatch(incomingChatMessage(data));
				}

				case Status.Unapproved:
				case Status.Deleted: {
					return dispatch(deleteChatMessage(Number(data.id)));
				}
			}
		};

		socket.addListener('update_chat_message', handleModeration);

		return () => {
			socket.removeListener('update_chat_message', handleModeration);
		};
	}, [socket, dispatch]);

	useEffect(() => {
		// create a mutation observer to set the button width so we can transition it to be perfectly centered
		if (moreButtonRef?.current) {
			// initialize on load:
			setMoreButtonWidth(moreButtonRef?.current?.getBoundingClientRect()?.width || 0);

			// listen for changes:
			const observer = new MutationObserver(() => {
				setMoreButtonWidth(moreButtonRef?.current?.getBoundingClientRect()?.width || 0);
			});
			observer.observe(moreButtonRef.current, {
				attributes: true,
				characterData: true,
				childList: true,
				subtree: true,
			});

			return () => {
				observer.disconnect();
			};
		}
	}, [moreButtonRef]);

	const toggleActive = useCallback(() => {
		onChatClose(!chatClosed);
	}, [chatClosed, onChatClose]);

	const handleTransitionEnd = useCallback(() => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
		}
	}, []);

	if (loading) return <WaitingIndicator />;
	if (!session) return null;

	const handleShowMore = () => {
		if (scrollContainerRef?.current) {
			scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
			setShowMoreButton(false);
		}
	};

	const content = useMemo(() => {
		return (
			<div
				className={classNames("live-chat-container", template, {
					'chat-overlay': !mobileOnly && session?.layout?.overlayChat || forceOverlay,
					'is-editor': isEditor,
					'is-open': !mobileOnly && !chatClosed,
					'move-chat-up': mobileOnly && !chatClosed,
				})}
				style={(mobileOnly && !chatClosed && !isEditor) ? { bottom: 0, top: 0, marginTop: 0, position: 'fixed', height: '100%' } : {}}
				ref={containerRef}
			>
				{mobileOnly ? (
					<div className="live-chat-header-mobile">
						<TypographyItem tagName="h6">{t("Chat")} • {comments.size} message{comments.size !== 1 ? "s" : ""}</TypographyItem>
						<button onClick={toggleActive} className="no-style"><Icon name={!chatClosed ? ICONS.CONTRACT : ICONS.EXPAND} color={COLORS.DEFAULT_GRAY} size={16} /></button>
					</div>
				) : (
					<div className="live-chat-header">
						<TypographyItem tagName="h5">{t("Chat")}</TypographyItem>
						<button onClick={toggleActive} className="no-style"><Icon name={ICONS.CLOSE} color={COLORS.WHITE} size={16} /></button>
					</div >
				)}

				<div className="feed-container" ref={scrollContainerRef}>
					<ChatFeed comments={comments} />

					{loadingComments && <div style={{ textAlign: 'center' }}><WaitingIndicator /></div>}
					<button
						ref={moreButtonRef}
						onClick={handleShowMore}
						style={{
							display: (!mobileOnly || (mobileOnly && !chatClosed)) && showMoreButton && scrollContainerRef?.current ? 'block' : 'none',
							position: 'sticky',
							top: 'calc(100% - 52px)',
							bottom: mobileOnly && !chatClosed ? 'auto' : '0',
							left: '0',
							transform: mobileOnly && !chatClosed
								? `translate(calc(100% + ${moreButtonWidth * 2}px), 0)`
								: `translate(calc(100% + ${moreButtonWidth}px), 0)`,
							transition: 'none',
						}}
					><Icon name={ICONS.ARROW_DOWN} color={COLORS.WHITE} size={16} /></button>
				</div>
				<div className={classNames("chat-actions-container", { extraPadding: extraPadding, chatClosed: chatClosed })}>
					<Promotions />
					<AddLiveComment
						setShowSpamFilterAlert={setShowSpamFilterAlert}
						showSpamFilterAlert={showSpamFilterAlert}
						session={session}
						language={language}
						eventBundle={eventBundle}
						isAdmin={isAdmin}
					/>
				</div>
			</div>
		);
	}, [
		moreButtonWidth,
		showMoreButton,
		template,
		mobileOnly,
		session,
		forceOverlay,
		isEditor,
		chatClosed,
		t,
		comments,
		toggleActive,
		loadingComments,
		extraPadding,
		showSpamFilterAlert,
		language,
		eventBundle,
		isAdmin,
	]);

	//live-chat-wrapper handles the in/out transitions, and conflicts severely with the mobile orientation. 
	//here we make the 
	return !mobileOnly ? (
		<div className={classNames("live-chat-wrapper", { 'active': !chatClosed, 'chat-overlay': session?.layout?.overlayChat || forceOverlay })} onTransitionEnd={handleTransitionEnd}>
			{content}
		</div>
	) : (
		<>{content}</>
	);

};

export default LiveChat;
